import state from '@/taskpane/state/sidebar-state'
import cleanRecipient from '@/taskpane/common/clean-recipient'
import cleanRecipientList from '@/taskpane/common/clean-recipient-list'
import { officeIsComposeMode } from './office-mail-mode'
import { officeDisplayName, officeUsername } from './office-user-profile'

import {
  officeApiMessage_BccAddAsync,
  officeApiMessage_BccGetAsync,
  officeApiMessage_CcAddAsync,
  officeApiMessage_CcGetAsync,
  officeApiMessage_FromGetAsync,
  officeApiMessage_ToAddAsync,
  officeApiMessage_ToGetAsync,
} from './async/office-api-message-async'
import { officeApiItem_SharedPropertiesLoadAsync } from './async/office-api-mailbox-item-async'
import uniquePeople from '../common/unique-people'

export function officeMailConversationId(): string {
  const apiConversationId = state.currentItem?.apiInfo?.conversationId
  const officeConversationId = Office.context.mailbox?.item?.conversationId
  return apiConversationId || officeConversationId || ''
}

export function officeMailConversationIndex(): string {
  return state.currentItem.apiInfo.conversationIndex || ''
}

export function officeMailInternetMessageId(): string {
  return state.currentItem.apiInfo.internetMessageId || Office.context.mailbox.item.internetMessageId || ''
}

export function officeMailDateTimeCreated(): Date {
  return Office.context.mailbox.item.dateTimeCreated || new Date()
}

export function officeMailDateTimeSent(): Date {
  if (officeIsComposeMode()) {
    return null
  } else {
    return state.currentItem.apiInfo.sentDate || officeMailDateTimeCreated()
  }
}

export async function officeMailTargetMailbox(): Promise<string> {
  if (!state.currentItem.targetMailbox) {
    const props = await officeApiItem_SharedPropertiesLoadAsync()
    if (props) {
      state.currentItem.targetMailbox = props?.targetMailbox ? props.targetMailbox.toLowerCase() : ''
    }
  }

  return state.currentItem.targetMailbox || ''
}

export async function officeMailIsSent(): Promise<boolean> {
  const [from, sharedMailboxUserEmail] = await Promise.all([officeMailRecipientsFrom(), officeMailTargetMailbox()])

  if (from?.emailAddress) {
    if (sharedMailboxUserEmail) {
      return sharedMailboxUserEmail === from.emailAddress.toLowerCase()
    } else {
      return from.emailAddress.toLowerCase() === officeUsername()
    }
  } else {
    return false
  }
}

export async function officeMailRecipientsFrom(): Promise<Office.EmailAddressDetails> {
  let fromDetails: Office.EmailAddressDetails

  if (officeIsComposeMode()) {
    fromDetails = await officeApiMessage_FromGetAsync()
    if (!fromDetails) {
      fromDetails = {
        displayName: officeDisplayName(),
        emailAddress: officeUsername(),
      } as Office.EmailAddressDetails
    }
  } else {
    fromDetails = Office.context.mailbox.item.from || ({} as Office.EmailAddressDetails)
  }

  fromDetails = cleanRecipient(fromDetails)
  if (!fromDetails.emailAddress && fromDetails.displayName) {
    if (fromDetails.displayName === officeDisplayName()) {
      fromDetails.emailAddress = officeUsername()
    } else {
      fromDetails.emailAddress = officeDisplayName()
    }
  }

  return fromDetails
}

export async function officeMailRecipientsTo(): Promise<Office.EmailAddressDetails[]> {
  let recipients: Office.EmailAddressDetails[]

  if (officeIsComposeMode()) {
    recipients = await officeApiMessage_ToGetAsync()
  } else {
    recipients = Office.context.mailbox.item.to || []
  }

  return cleanRecipientList(recipients)
}

export async function officeMailRecipientsCc(): Promise<Office.EmailAddressDetails[]> {
  let recipients: Office.EmailAddressDetails[]

  if (officeIsComposeMode()) {
    recipients = await officeApiMessage_CcGetAsync()
  } else {
    recipients = Office.context.mailbox.item.cc || []
  }

  return cleanRecipientList(recipients)
}

export async function officeMailRecipientsBcc(): Promise<Office.EmailAddressDetails[]> {
  let recipients: Office.EmailAddressDetails[]

  if (officeIsComposeMode()) {
    recipients = await officeApiMessage_BccGetAsync()
  } else {
    recipients = state.currentItem.apiInfo.bcc || []
  }

  return cleanRecipientList(recipients)
}

export async function officeMailRecipients(): Promise<Office.EmailAddressDetails[]> {
  const [from, to, cc, bcc] = await Promise.all([
    officeMailRecipientsFrom(),
    officeMailRecipientsTo(),
    officeMailRecipientsCc(),
    officeMailRecipientsBcc(),
  ])

  if (officeIsComposeMode()) {
    return uniquePeople(to.concat(cc, bcc))
  } else {
    return uniquePeople([from].concat(to, cc, bcc))
  }
}

export async function officeMailRecipientsEmailAddresses(): Promise<string[]> {
  return (await officeMailRecipients()).map((recipient) => recipient.emailAddress)
}

export async function officeMailRecipientsAddToEmails(emails: Office.EmailAddressDetails[]): Promise<boolean> {
  return officeMailRecipientsAdd(officeMailRecipientsTo, officeApiMessage_ToAddAsync, emails)
}

export async function officeMailRecipientsAddCcEmails(emails: Office.EmailAddressDetails[]): Promise<boolean> {
  return officeMailRecipientsAdd(officeMailRecipientsCc, officeApiMessage_CcAddAsync, emails)
}

export async function officeMailRecipientsAddBccEmails(emails: Office.EmailAddressDetails[]): Promise<boolean> {
  return officeMailRecipientsAdd(officeMailRecipientsBcc, officeApiMessage_BccAddAsync, emails)
}

async function officeMailRecipientsAdd(getRecipients: Function, addRecipients: Function, emails: Office.EmailAddressDetails[]): Promise<boolean> {
  const addedEmails = dedupEmailsToAdd(await getRecipients(), emails)
  let addedRecipientCount = 0
  const promises: Promise<boolean>[] = []

  while (addedRecipientCount < addedEmails.length) {
    const addPromise = addRecipients(addedEmails.slice(addedRecipientCount, addedRecipientCount + 100))
    promises.push(addPromise)
    addedRecipientCount += 100
  }

  const results = await Promise.all(promises)
  return results.every((result) => !!result)
}

function dedupEmailsToAdd(currentEmails: Office.EmailAddressDetails[], newEmails: Office.EmailAddressDetails[]): Office.EmailAddressDetails[] {
  return newEmails.filter((newEmail) => {
    return !currentEmails.some((currentEmail) => {
      return currentEmail.emailAddress.toLocaleLowerCase() === newEmail.emailAddress.toLocaleLowerCase()
    })
  })
}

const exportFunctions = {
  conversationId: officeMailConversationId,
  conversationIndex: officeMailConversationIndex,
  internetMessageId: officeMailInternetMessageId,
  dateTimeCreated: officeMailDateTimeCreated,
  dateTimeSent: officeMailDateTimeSent,
  recipients: officeMailRecipients,
  emailAddresses: officeMailRecipientsEmailAddresses,
  from: officeMailRecipientsFrom,
  to: officeMailRecipientsTo,
  cc: officeMailRecipientsCc,
  bcc: officeMailRecipientsBcc,
  addTo: officeMailRecipientsAddToEmails,
  addCc: officeMailRecipientsAddCcEmails,
  addBcc: officeMailRecipientsAddBccEmails,
  targetMailbox: officeMailTargetMailbox,
  isSentItem: officeMailIsSent,
}

export default exportFunctions
